import { Outlet, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Button,
  Container,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useAuthStore } from '../../stores/root_store';

const navLinks = [
  { label: 'Personal Assistants', path: '/personal-assistants' },
  { label: 'Dedicated Assistants', path: '/dedicated-assistant' },
  { label: 'FAQ', path: '/faq' },
];

export const MarketingLayout = observer(() => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const authStore = useAuthStore();

  const dashboardPath = authStore.isAssistant ? '/assistant' : '/dashboard';

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" color="transparent" elevation={0}>
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            <Box
              sx={{ flexGrow: 1, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              onClick={() => navigate('/home')}
            >
              <img
                src="/Fancy-Hands-Logo.png"
                alt="Fancy Hands"
                style={{ height: 32 }}
              />
            </Box>

            {isMobile ? (
              <>
                <IconButton
                  size="large"
                  edge="end"
                  color="inherit"
                  onClick={handleMenuOpen}
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  {navLinks.map((link) => (
                    <MenuItem
                      key={link.path}
                      onClick={() => {
                        navigate(link.path);
                        handleMenuClose();
                      }}
                    >
                      {link.label}
                    </MenuItem>
                  ))}
                  {authStore.isAuthenticated ? (
                    <MenuItem
                      onClick={() => {
                        navigate(dashboardPath);
                        handleMenuClose();
                      }}
                    >
                      Dashboard
                    </MenuItem>
                  ) : (
                    <>
                      <MenuItem
                        onClick={() => {
                          navigate('/login');
                          handleMenuClose();
                        }}
                      >
                        Sign In
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          navigate('/register');
                          handleMenuClose();
                        }}
                      >
                        Sign Up
                      </MenuItem>
                    </>
                  )}
                </Menu>
              </>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center">
                {navLinks.map((link) => (
                  <Button
                    key={link.path}
                    color="inherit"
                    component={RouterLink}
                    to={link.path}
                  >
                    {link.label}
                  </Button>
                ))}
                {authStore.isAuthenticated ? (
                  <Button color="inherit" onClick={() => navigate(dashboardPath)}>
                    Dashboard
                  </Button>
                ) : (
                  <>
                    <Button color="inherit" onClick={() => navigate('/login')}>
                      Sign In
                    </Button>
                    <Button variant="contained" onClick={() => navigate('/register')}>
                      Sign Up
                    </Button>
                  </>
                )}
              </Stack>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Outlet />

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 4,
          mt: 'auto',
          bgcolor: 'grey.900',
          color: 'grey.300',
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'center', md: 'flex-start' }}
            spacing={3}
          >
            <Box>
              <Typography variant="body2" color="grey.500">
                &copy; {new Date().getFullYear()} Fancy Hands. All rights reserved.
              </Typography>
            </Box>
            <Stack direction="row" spacing={3}>
              <Typography
                component={RouterLink}
                to="/faq"
                variant="body2"
                sx={{ color: 'grey.400', textDecoration: 'none', '&:hover': { color: 'white' } }}
              >
                FAQ
              </Typography>
              <Typography
                component="a"
                href="mailto:contact@fancyhands.com"
                variant="body2"
                sx={{ color: 'grey.400', textDecoration: 'none', '&:hover': { color: 'white' } }}
              >
                Contact
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
});
